import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { ArrowsClockwise, TrendUp, TrendDown, CircleNotch, Lightning, Warning, ArrowSquareOut, Books } from "@phosphor-icons/react";
import { tradingViewStockUrl, screenerStockUrl } from "../lib/tradingview";

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : "—";

function MoverRow({ m, positive, index }) {
  const tone = positive ? "text-pos" : "text-neg";
  const sym = m.symbol.replace(".NS", "");
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.04 * index }}
      className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-subtle last:border-b-0 hover:bg-surface-hover transition"
      data-testid={`mover-row-${m.symbol}`}
    >
      <div className="col-span-1 font-mono text-[10px] text-muted-fg">#{index + 1}</div>
      <div className="col-span-3 min-w-0">
        <div className="font-mono text-sm font-semibold tracking-wide text-primary-fg truncate">
          {sym}
        </div>
        <div className="text-[11px] text-muted-fg truncate">{m.name}</div>
      </div>
      <div className="col-span-2 text-right">
        <div className="font-mono text-sm tabular-nums text-primary-fg">₹{fmt(m.last)}</div>
        <div className="text-[10px] text-muted-fg font-mono">
          from ₹{fmt(m.prev_close)}
        </div>
      </div>
      <div className={"col-span-2 text-right font-mono text-sm tabular-nums flex items-center justify-end gap-1 " + tone}>
        {positive ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
        {m.change_pct > 0 ? "+" : ""}
        {m.change_pct.toFixed(2)}%
      </div>
      <div className="col-span-2 text-right">
        <span
          className={
            "inline-flex items-center gap-1 px-2 py-1 rounded-sm border text-[10px] font-mono tabular-nums " +
            (m.volume_multiple >= 3
              ? positive
                ? "bg-pos-soft border-pos-soft text-pos"
                : "bg-neg-soft border-neg-soft text-neg"
              : "bg-surface-hover border-subtle text-secondary-fg")
          }
        >
          <Lightning size={10} weight="fill" />
          {m.volume_multiple}× vol
        </span>
      </div>
      <div className="col-span-2 text-right">
        <div className="inline-flex items-center gap-1.5">
          <a
            href={tradingViewStockUrl(m.symbol)}
            target="_blank"
            rel="noreferrer"
            data-testid={`mover-tv-${m.symbol}`}
            className="inline-flex items-center gap-1 px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:text-pos hover:border-pos/40 transition"
            title={`Open ${sym} chart on TradingView`}
          >
            <ArrowSquareOut size={11} weight="bold" />
            TV
          </a>
          <a
            href={screenerStockUrl(m.symbol)}
            target="_blank"
            rel="noreferrer"
            data-testid={`mover-screener-${m.symbol}`}
            className="inline-flex items-center gap-1 px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:text-mod hover:border-mod/40 transition"
            title={`Open ${sym} fundamentals on Screener.in`}
          >
            <Books size={11} weight="bold" />
            SCR
          </a>
        </div>
      </div>
    </motion.div>
  );
}

function MoversPanel({ title, subtitle, data, positive, testid }) {
  const style = positive ? "border-pos-soft bg-pos-soft" : "border-neg-soft bg-neg-soft";
  const toneHeading = positive ? "text-pos" : "text-neg";
  return (
    <section className="border border-subtle rounded-sm overflow-hidden" data-testid={testid}>
      <div className={`p-4 border-b border-subtle ${style}`}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className={`flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest ${toneHeading}`}>
              {positive ? <TrendUp size={12} weight="bold" /> : <Warning size={12} weight="bold" />}
              {title}
            </div>
            <div className="font-mono text-lg tracking-tight text-primary-fg mt-1">
              {data?.name || "—"}
            </div>
          </div>
          <div className={"font-mono text-2xl tabular-nums " + toneHeading}>
            {data?.change_pct != null
              ? `${data.change_pct > 0 ? "+" : ""}${data.change_pct.toFixed(2)}%`
              : "—"}
          </div>
        </div>
        <div className="text-[11px] text-secondary-fg mt-1">{subtitle}</div>
      </div>
      {data?.movers?.length ? (
        data.movers.map((m, i) => (
          <MoverRow key={m.symbol} m={m} positive={positive} index={i} />
        ))
      ) : (
        <div className="p-6 text-sm text-muted-fg text-center">
          No qualifying stocks yet — try Refresh after 9:30 IST.
        </div>
      )}
    </section>
  );
}

export default function SectorMovers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sectors/movers");
      setData(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("sector movers load failed:", e);
    }
    finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/sectors/movers/refresh");
      setData(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("sector movers refresh failed:", e);
    }
    finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 border border-subtle bg-surface rounded-sm flex items-center gap-3" data-testid="movers-loading">
        <CircleNotch className="animate-spin text-mod" size={18} weight="bold" />
        <div className="text-sm text-secondary-fg">Crunching sector movers…</div>
      </div>
    );
  }

  const updatedAt = data?.updated_at
    ? new Date(data.updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  return (
    <div data-testid="sector-movers" className="mt-6">
      <div className="flex items-baseline justify-between mb-4 border-b border-subtle pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg">
            /// Sector Top Movers
          </div>
          <div className="text-xs text-muted-fg mt-1">
            Strongest &amp; weakest sector · ranked by volume surge · updated {updatedAt} · auto 09:25 IST
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          data-testid="movers-refresh"
          className="px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest hover:bg-surface-hover text-secondary-fg hover:text-primary-fg transition flex items-center gap-2"
        >
          {refreshing ? (
            <CircleNotch size={12} weight="bold" className="animate-spin" />
          ) : (
            <ArrowsClockwise size={12} weight="bold" />
          )}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MoversPanel
          title="Top 5 Strong · Strongest Sector"
          subtitle="Volume-surge leaders riding the sector wave"
          data={data?.strongest}
          positive={true}
          testid="panel-strong-movers"
        />
        <MoversPanel
          title="Top 5 Weak · Weakest Sector"
          subtitle="High-volume distribution — candidates for shorts / avoid"
          data={data?.weakest}
          positive={false}
          testid="panel-weak-movers"
        />
      </div>
    </div>
  );
}
